import type { NavigationSection } from "@/config/navigation";
import { navigation } from "@/config/navigation";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { cloneElement, useEffect, useState } from "react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";

interface CommandPaletteProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showTrigger?: boolean;
  navigationItems?: NavigationSection[];
}

export function CommandPalette({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showTrigger = true,
  navigationItems = navigation,
}: CommandPaletteProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const navigate = useNavigate();

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen]);

  const handleSelect = (url: string, isExternal?: boolean) => {
    setOpen(false);
    if (isExternal) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      navigate({ to: url });
    }
  };

  return (
    <>
      {showTrigger && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center justify-between gap-2 min-w-0 w-full sm:w-64 px-3 sm:px-4 py-2.5 text-sm text-muted-foreground bg-background border rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors whitespace-nowrap"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Search className="h-4 w-4 shrink-0" />
            <span className="truncate">Search pages...</span>
          </div>
          <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex shrink-0">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>
      )}

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        className="max-w-2xl w-[calc(100vw-2rem)] sm:w-full"
      >
        <CommandInput
          placeholder="Type to search pages..."
          className="h-14 text-base"
        />
        <CommandList className="max-h-[500px]">
          <CommandEmpty className="py-8 text-center text-base">
            No results found.
          </CommandEmpty>
          {navigationItems.map((section) => (
            <CommandGroup
              key={section.title}
              heading={section.title}
              className="px-2"
            >
              {section.items.map((item) => (
                <CommandItem
                  key={item.url}
                  value={`${item.title} ${item.keywords?.join(" ") || ""}`}
                  onSelect={() => handleSelect(item.url, item.isExternal)}
                  className="flex items-center gap-4 px-4 py-4 cursor-pointer rounded-lg"
                >
                  {cloneElement(item.icon, { className: "h-6 w-6 shrink-0" })}
                  <div className="flex flex-col flex-1">
                    <span className="font-medium text-base">{item.title}</span>
                    {item.keywords && item.keywords.length > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {section.title}
                      </span>
                    )}
                  </div>
                  {item.isExternal && (
                    <span className="ml-auto text-sm text-muted-foreground">
                      External
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
